import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import HeroSystem from "@/components/HeroSystem";
import { ProcessSteps, FounderPilot } from "@/components/marketing";

const FOCUS = [
  { t: "Indian Languages", d: "Native language understanding, translation, writing, annotation and evaluation." },
  { t: "Law", d: "Legal reasoning, document review, compliance and policy evaluation." },
  { t: "Healthcare", d: "Clinical knowledge, medical reasoning and healthcare language evaluation." },
  { t: "Coding", d: "Code quality, debugging, reasoning and technical evaluation." },
];

const WHY = [
  { t: "Specialised Training", d: "Learn how AI evaluation differs from ordinary domain work." },
  { t: "Flexible, Remote Work", d: "Evaluate on your own schedule from anywhere, without commuting or rigid office hours." },
  { t: "Competitive Compensation", d: "Earn for approved project work with straightforward, reliable payouts." },
  { t: "Hands-on Human Support", d: "Work alongside dedicated community coordinators who answer questions and guide you through complex tasks." },
  { t: "FIAD Vetted Credential", d: "Earn a verified status that signals your readiness for high-standard AI evaluation work." },
  { t: "Meaningful AI Impact", d: "Directly influence how next-generation foundation models interpret your language and field." },
];

const LAB_OUTCOMES = [
  "Higher inter-annotator evaluation consistency",
  "Faster evaluator onboarding with lower rubric ramp-up time",
  "Precise, context-aware domain judgment",
  "Smaller, calibrated evaluator cohorts instead of noisy crowds",
  "Lower mid-project contributor drop-offs",
  "Reduced operational QA and project friction",
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />

      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <HeroSystem />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-24 sm:pt-32 sm:pb-32">
          <p className="eyebrow text-[#C79447] reveal">Indian Languages · Law · Healthcare · Coding</p>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-[4.6rem] font-semibold tracking-tight leading-[1.03] max-w-4xl reveal reveal-1">
            Specialized human intelligence for frontier AI
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-[#D4D0C6] leading-relaxed max-w-2xl reveal reveal-2">
            Domain expertise isn't enough. FIAD trains and vets specialists to rigorously evaluate AI systems—giving frontier models human judgment they can trust.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 reveal reveal-3">
            <button data-testid="hero-expert-cta" onClick={() => navigate("/experts")} className="inline-flex items-center justify-center gap-2 bg-[#F6F3EC] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-white transition-colors">Become a FIAD Expert <ArrowRight className="h-4 w-4" /></button>
            <Link data-testid="hero-pilot-cta" to="/pilot" className="inline-flex items-center justify-center gap-2 border border-white/25 text-[#F6F3EC] px-7 py-3.5 text-sm font-medium hover:border-white/60 transition-colors">Discuss a Pilot</Link>
          </div>
          <p className="mt-5 inline-flex items-center gap-2 text-sm text-[#A1A1AA] reveal reveal-4"><span className="h-1.5 w-1.5 rounded-full bg-[#C79447]" />Direct scoping with our founding team · Response within 24 hours</p>
          <p className="mt-12 font-display text-xl sm:text-2xl text-[#F6F3EC]/70 tracking-tight max-w-xl reveal reveal-4">We don't build AI models. We build better evaluators.</p>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#71717A]">The problem</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Domain expertise is not evaluation expertise.</h2>
            <div className="mt-8 border-l-2 border-[#C79447]/50 pl-5">
              <p className="font-mono-num text-sm text-[#C79447]">EXPERTISE → TRAINING → JUDGMENT</p>
              <p className="mt-2 text-sm text-[#71717A] leading-relaxed">AI evaluation is its own discipline.</p>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-12 space-y-5 text-lg text-[#A1A1AA] leading-relaxed">
            <p>Frontier models depend on people with deep, specialized knowledge—whether in regional languages, law, healthcare, or code. But an outstanding professional won't instinctively know how to red-team a model, spot subtle language or domain errors, or systematically catch edge-case hallucinations.</p>
            <p><span className="text-[#F6F3EC]">AI evaluation is its own discipline.</span> FIAD trains, assesses, and vets specialists so their domain expertise translates into rigorous, reliable AI evaluation.</p>
          </div>
        </div>
      </section>

      {/* WHAT FIAD ACTUALLY DOES */}
      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="mb-12">
            <p className="eyebrow text-[#71717A]">What FIAD actually does</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight">We build evaluation-ready domain experts.</h2>
          </div>
          <ProcessSteps dark />
          <p className="mt-6 text-sm text-[#71717A]">Becoming FIAD Vetted does not guarantee project selection or employment.</p>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#71717A]">Focus areas</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">Expertise where human judgment matters.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
            {FOCUS.map((f, i) => (
              <div key={f.t} data-testid={`focus-${i}`} className="group border-b border-r border-white/10 p-8 sm:p-10 hover:bg-white/[0.03] transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-[#F6F3EC]">{f.t}</h3>
                  <span className="font-mono-num text-lg text-white/15 group-hover:text-[#C79447] transition-colors">0{i + 1}</span>
                </div>
                <p className="mt-4 text-[#A1A1AA] leading-relaxed max-w-md">{f.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#71717A]">Additional specialised domains will open over time.</p>
        </div>
      </section>

      {/* CURRENTLY OPEN */}
      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28" data-testid="now-onboarding">
          <div className="border border-[#C79447]/30 bg-gradient-to-b from-[#C79447]/[0.06] to-transparent p-8 sm:p-12">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-[#C79447] opacity-60 animate-ping" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C79447]" /></span>
              <span className="eyebrow text-[#C79447]">Now onboarding</span>
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.05]">Bengali Linguistic Experts</h2>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
              <p className="lg:col-span-7 text-lg text-[#D4D0C6] leading-relaxed">We're currently reviewing applications from people with genuine Bengali-language expertise — native speakers, bilingual speakers, and professionals with substantial relevant experience.</p>
              <div className="lg:col-span-5 flex flex-col gap-4">
                <button data-testid="apply-bengali-btn" onClick={() => navigate("/apply")} className="inline-flex items-center justify-between gap-2 bg-[#C79447] text-[#0A0A0A] px-7 py-4 text-sm font-medium hover:brightness-110 transition-all">Apply for the Bengali Linguist Cohort <ArrowRight className="h-4 w-4" /></button>
                <p className="text-sm text-[#71717A] leading-relaxed">Other domain? Your field isn't open yet, but that doesn't mean we won't need it.</p>
                <button data-testid="join-waitlist-btn" onClick={() => navigate("/waitlist")} className="inline-flex items-center gap-2 text-sm text-[#F6F3EC] link-underline w-fit">Join the Waitlist <ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY EXPERTS CHOOSE FIAD */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#71717A]">Why experts choose FIAD</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">Built around specialised judgment.</h2>
          <div className="mt-12 divide-y divide-white/10 border-t border-white/10">
            {WHY.map((w, i) => (
              <div key={w.t} className="grid grid-cols-12 gap-4 sm:gap-8 py-7 group">
                <span className="col-span-2 sm:col-span-1 font-mono-num text-sm text-white/25 group-hover:text-[#C79447] transition-colors pt-1">0{i + 1}</span>
                <h3 className="col-span-10 sm:col-span-4 font-display text-lg sm:text-xl font-medium tracking-tight text-[#F6F3EC]">{w.t}</h3>
                <p className="col-span-12 sm:col-span-7 text-[#A1A1AA] leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR AI LABS */}
      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="eyebrow text-[#C79447]">Built for AI labs</p>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Better evaluators. Faster onboarding. Higher-quality outputs.</h2>
              <p className="mt-6 text-lg text-[#A1A1AA] leading-relaxed">Most AI data pipelines optimize for raw scale. FIAD optimizes for judgment quality. Instead of sourcing thousands of unvetted, anonymous workers, we curate and train compact cohorts of domain specialists who understand the nuances of both their field and the evaluation rubric.</p>
            </div>
            <div className="lg:col-span-7">
              <p className="eyebrow text-[#71717A] mb-4">Designed to improve</p>
              <div className="border-t border-white/10">
                {LAB_OUTCOMES.map((o, i) => (
                  <div key={i} className="flex items-center gap-5 border-b border-white/10 py-4">
                    <span className="font-mono-num text-sm text-[#C79447]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#D4D0C6]">{o}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-[#71717A] leading-relaxed">These are intended outcomes, not guarantees. Results depend on project design, evaluation requirements, rubric quality, and other factors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI LAB PILOT CTA */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="eyebrow text-[#71717A]">For AI labs</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12] max-w-2xl">Need specialised human judgment for AI evaluation?</h2>
            <p className="mt-6 text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">If you're building LLMs, multimodal systems, reasoning models or domain-specific AI products, we'd love to understand your evaluation needs.</p>
          </div>
          <div className="lg:col-span-5 lg:pt-10">
            <FounderPilot heading="Discuss a pilot." testId="home-founder-pilot" />
          </div>
        </div>
      </section>

      {/* EXPERT CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-6">
              <p className="eyebrow text-[#C79447]">For experts</p>
              <h2 className="mt-5 font-display text-3xl sm:text-5xl font-medium tracking-tight leading-[1.08]">Your expertise can help build better AI — and earn from it.</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-4 space-y-6">
              <p className="text-lg text-[#A1A1AA] leading-relaxed">FIAD is building a trusted network of specialised experts who contribute to AI evaluation across languages and high-context domains. If your domain is currently open, apply today. Otherwise, join the waitlist and we'll notify you when applications open.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button data-testid="expert-cta-btn" onClick={() => navigate("/apply")} className="inline-flex items-center gap-2 bg-[#F6F3EC] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-white transition-colors">Become a FIAD Expert <ArrowRight className="h-4 w-4" /></button>
                <button data-testid="expert-waitlist-btn" onClick={() => navigate("/waitlist")} className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-sm font-medium hover:border-white/60 transition-colors">Join Waitlist</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
